import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOnboardingTour } from "../../../context/OnboardingTourContext";
import { useAdvancedWelcomeTourOptional } from "../../../context/AdvancedWelcomeTourContext";
import { useOnboardingTourModules } from "../../../hooks/useOnboardingTourModules";
import {
  ONBOARDING_OPEN_EVENT,
  dismissOnboardingForSession,
  isOnboardingSessionDismissed,
  resetOnboardingTutorial,
} from "../../../utils/onboardingTutorialStorage";
import {
  isAdvancedWelcomeTourComplete,
  startAdvancedWelcomeTour,
} from "../../../utils/advancedWelcomeTourStorage";
import "../../../assets/css/onboarding-tutorial.css";
import "../../../assets/css/interactive-tour.css";
import "../../../assets/css/advanced-welcome-tour.css";

export default function OnboardingTutorial() {
  const { t } = useTranslation();
  const location = useLocation();
  const { startModule, isActive: tourActive } = useOnboardingTour();
  const advancedTour = useAdvancedWelcomeTourOptional();
  const [refreshKey, setRefreshKey] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [advancedComplete, setAdvancedComplete] = useState(() => isAdvancedWelcomeTourComplete());
  const [sessionDismissed, setSessionDismissed] = useState(
    () => isOnboardingSessionDismissed()
  );

  const hideOnWizardPage = location.pathname.replace(/\/+$/, "").endsWith("/start-now");

  const { loading, modules, progress, allComplete, completedCount, totalModules, refresh } =
    useOnboardingTourModules({ refreshKey });

  const bumpRefresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      setSessionDismissed(false);
      setExpanded(true);
      bumpRefresh();
    };

    const handleAdvancedComplete = () => setAdvancedComplete(true);

    window.addEventListener(ONBOARDING_OPEN_EVENT, handleOpen);
    window.addEventListener("onboarding-tour:progress", bumpRefresh);
    window.addEventListener("advanced-welcome-tour:complete", handleAdvancedComplete);
    return () => {
      window.removeEventListener(ONBOARDING_OPEN_EVENT, handleOpen);
      window.removeEventListener("onboarding-tour:progress", bumpRefresh);
      window.removeEventListener("advanced-welcome-tour:complete", handleAdvancedComplete);
    };
  }, [bumpRefresh]);

  useEffect(() => {
    bumpRefresh();
  }, [location.pathname, bumpRefresh]);

  useEffect(() => {
    const handleFocus = () => bumpRefresh();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [bumpRefresh]);

  const handleRemindLater = () => {
    dismissOnboardingForSession();
    setSessionDismissed(true);
    setExpanded(false);
  };

  const handleReset = () => {
    resetOnboardingTutorial({ resetQrStep: true });
    setAdvancedComplete(false);
    setSessionDismissed(false);
    setExpanded(true);
    bumpRefresh();
    void refresh();
  };

  const handleStartAdvancedTour = () => {
    setExpanded(false);
    startAdvancedWelcomeTour();
  };

  const handleStartTour = (moduleId) => {
    setExpanded(false);
    void startModule(moduleId);
  };

  if (hideOnWizardPage || loading) {
    return null;
  }

  if (tourActive || advancedTour?.isActive || !expanded) {
    return null;
  }

  return (
    <div className="onboarding-tutorial-panel onboarding-tutorial-panel--wide" role="dialog" aria-labelledby="onboarding-tutorial-title">
      <div className="onboarding-tutorial-panel__header">
        <div>
          <p className="onboarding-tutorial-panel__eyebrow">{t("onboardingEyebrow")}</p>
          <h2 id="onboarding-tutorial-title">{t("onboardingTitle")}</h2>
          <p className="onboarding-tutorial-panel__subtitle">{t("onboardingSubtitleInteractive")}</p>
        </div>
        <div className="onboarding-tutorial-panel__header-actions">
          <button
            type="button"
            className="onboarding-tutorial-icon-btn"
            onClick={() => setExpanded(false)}
            aria-label={t("onboardingMinimize")}
          >
            <i className="fas fa-minus" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="onboarding-tutorial-icon-btn"
            onClick={handleRemindLater}
            aria-label={t("onboardingRemindLater")}
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="onboarding-tutorial-progress" aria-hidden="true">
        <div className="onboarding-tutorial-progress__bar" style={{ width: `${progress}%` }} />
      </div>
      <p className="onboarding-tutorial-progress__label">
        {t("onboardingModulesProgress", {
          done: completedCount,
          total: totalModules,
          percent: progress,
        })}
      </p>

      <div className={`onboarding-tutorial-advanced${advancedComplete ? " is-complete" : ""}`}>
        <span className="onboarding-tutorial-advanced__badge">{t("advTourModuleBadge")}</span>
        <div className="onboarding-tutorial-advanced__head">
          <span className="onboarding-tutorial-advanced__icon" aria-hidden="true">
            <i className="fas fa-route" />
          </span>
          <div>
            <h3>{t("advTourModuleTitle")}</h3>
            <p>{t("advTourModuleDesc")}</p>
          </div>
        </div>
        <div className="onboarding-tutorial-advanced__meta">
          <span className="onboarding-tutorial-module__progress">
            {advancedComplete ? t("onboardingDone") : t("advTourModuleStatus")}
          </span>
          <button
            type="button"
            className={`onboarding-tutorial-advanced__cta${
              advancedComplete ? " onboarding-tutorial-advanced__cta--ghost" : ""
            }`}
            onClick={handleStartAdvancedTour}
          >
            {advancedComplete ? t("advTourReplay") : t("advTourStart")}
          </button>
        </div>
      </div>

      <ul className="onboarding-tutorial-modules">
        {modules.map((module) => {
          const statusClass = module.complete
            ? "is-complete"
            : module.inProgress
              ? "is-progress"
              : "";

          return (
            <li key={module.id} className={`onboarding-tutorial-module ${statusClass}`}>
              <div className="onboarding-tutorial-module__head">
                <span className="onboarding-tutorial-module__icon" aria-hidden="true">
                  <i className={`fas ${module.icon}`} />
                </span>
                <div>
                  <h3 className="onboarding-tutorial-module__title">{module.title}</h3>
                  <p className="onboarding-tutorial-module__desc">{module.description}</p>
                </div>
              </div>
              <div className="onboarding-tutorial-module__meta">
                <span className="onboarding-tutorial-module__progress">
                  {module.complete
                    ? t("onboardingDone")
                    : t("tourModuleStepsProgress", {
                        done: module.viewedSteps,
                        total: module.totalSteps,
                      })}
                </span>
                <button
                  type="button"
                  className={`onboarding-tutorial-module__cta${
                    module.complete ? " onboarding-tutorial-module__cta--ghost" : ""
                  }`}
                  onClick={() => handleStartTour(module.id)}
                >
                  {module.complete ? t("tourReplayModule") : t("tourStartInteractive")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="onboarding-tutorial-panel__footer">
        <button type="button" className="onboarding-tutorial-link-btn" onClick={handleRemindLater}>
          {t("onboardingRemindLater")}
        </button>
        <span className="onboarding-tutorial-panel__footer-sep" aria-hidden="true">
          ·
        </span>
        <button type="button" className="onboarding-tutorial-link-btn" onClick={handleReset}>
          {t("onboardingReset")}
        </button>
      </div>
    </div>
  );
}
